import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { selectActiveOrg, useAuthStore } from '../auth/store';

// ---------------------------------------------------------------------------
// Types — mirror backend/src/modules/invoices/recurring.service.ts
// ---------------------------------------------------------------------------

export type Cadence =
  | 'weekly'
  | 'biweekly'
  | 'semimonthly'
  | 'monthly'
  | 'quarterly'
  | 'semiannual'
  | 'annual';

export const CADENCE_OPTIONS: Array<{ value: Cadence; label: string }> = [
  { value: 'monthly', label: 'Every month' },
  { value: 'quarterly', label: 'Every quarter' },
  { value: 'semiannual', label: 'Twice a year' },
  { value: 'annual', label: 'Every year' },
  { value: 'semimonthly', label: 'Twice a month' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'weekly', label: 'Every week' },
];

export interface RecurringTemplate {
  id: string;
  client: { id: string; name: string };
  clientId: string;
  agreement: { id: string; name: string } | null;
  agreementId: string | null;
  /** Empty = one line on `agreement`; otherwise each line credits its own agreement. */
  lines: Array<{
    id: string;
    description: string | null;
    amount: number;
    agreementId: string | null;
    agreement: { id: string; name: string } | null;
  }>;
  /** The raw subject, `%variables%` intact — what the editor edits. */
  subject: string;
  /** What the next invoice will actually say. */
  subjectPreview: string;
  unknownVariables: string[];
  amount: number;
  cadence: Cadence;
  cadenceLabel: string;
  nextIssueDate: string;
  nextIssueLabel: string;
  upcoming: string[];
  dayOfMonth: number | null;
  netDays: number;
  kind: string;
  isActive: boolean;
  isDue: boolean;
  lastIssuedAt: string | null;
  note: string | null;
  /**
   * Send without a human. A calendar schedule emails the current period's
   * invoice at 09:00 local; a Stripe-paid one attaches the paid invoice to the
   * charge's receipt. See `backend/src/modules/invoices/recurring-auto-send.ts`.
   */
  autoSend: boolean;
  /** What the last automatic send did. */
  lastAutoSend: LastAutoSend | null;
  /** Set when a Stripe SUBSCRIPTION pays this schedule — see
   *  `backend/src/modules/invoices/subscription-billing.ts`. */
  stripe: { subscriptionId: string; lastCharge: StripeLastCharge | null } | null;
}

export interface LastAutoSend {
  at: string;
  invoiceId: string | null;
  number: string | null;
  outcome: 'sent' | 'filed' | 'refused' | 'failed' | 'held';
  to: string | null;
  message: string;
}

/** What the most recent subscription charge did. */
export interface StripeLastCharge {
  at: string;
  stripeInvoiceId: string;
  amount: number;
  outcome: 'recorded' | 'already-recorded' | 'recorded-by-hand' | 'in-harvest' | 'failed';
  invoiceId: string | null;
  invoiceNumber: string | null;
  message: string;
}

export interface StripeSubscription {
  id: string;
  status: string;
  customerId: string | null;
  customerName: string | null;
  customerEmail: string | null;
  amount: number;
  currency: string;
  interval: string | null;
  intervalCount: number | null;
  cadence: Cadence | null;
  nextChargeDate: string | null;
  cancelAtPeriodEnd: boolean;
  description: string | null;
  products: string[];
  linkedTemplate: LinkTemplateRow | null;
  suggestion: { templateId: string; reasons: string[]; template: LinkTemplateRow | null } | null;
}

export interface LinkTemplateRow {
  id: string;
  clientName: string;
  subjectPreview: string;
  amount: number;
  cadence: Cadence;
  cadenceLabel: string;
  isActive: boolean;
  stripeSubscriptionId: string | null;
}

export interface StripeSubscriptionList {
  connected: boolean;
  reason: string | null;
  subscriptions: StripeSubscription[];
  /** Links whose subscription no longer charges on the connected account. */
  missing: Array<{ subscriptionId: string; template: LinkTemplateRow }>;
  templates: LinkTemplateRow[];
}

export type ChargeAction = 'recorded' | 'pay' | 'create' | 'recorded-by-hand' | 'in-harvest';

export interface StripeChargeRow {
  stripeInvoiceId: string;
  stripeNumber: string | null;
  paidOn: string;
  amount: number;
  currency: string;
  action: ChargeAction;
  invoice: { id: string; number: string; state: string } | null;
  sentence: string;
}

export interface RecurringList {
  today: string;
  templates: RecurringTemplate[];
  variables: string[];
  summary: {
    active: number;
    inactive: number;
    dueCount: number;
    dueAmount: number;
    next12Months: number;
    /** Average monthly worth — quarterly ÷ 3, annual ÷ 12. Not a billing date. */
    perMonth: number;
    /** Active schedules that are not monthly. */
    nonMonthly: number;
    /** Active schedules a Stripe subscription pays. */
    stripeLinked: number;
  };
}

export interface GeneratedInvoice {
  templateId: string;
  clientName: string;
  invoiceId: string;
  number: string;
  issueDate: string;
  subject: string;
  amount: number;
}

export interface RunResult {
  generated: GeneratedInvoice[];
  templates: number;
  cappedTemplates: string[];
  errors: Array<{ templateId: string; message: string }>;
}

export interface RecurringInput {
  clientId: string;
  subject: string;
  amount: number;
  nextIssueDate: string;
  cadence?: Cadence;
  agreementId?: string | null;
  netDays?: number;
  kind?: string;
  isActive?: boolean;
  note?: string | null;
  autoSend?: boolean;
  /** Two or more lines, each with its own agreement; [] = back to one line. */
  lines?: Array<{ description: string | null; amount: number; agreementId: string | null }>;
}

/** What crediting a schedule's past invoices does — `apply: false` only says. */
export interface LinkPastResult {
  applied: boolean;
  linked: string[];
  split: string[];
  skipped: Array<{ number: string; reason: string }>;
}

// ---------------------------------------------------------------------------
// Hooks — keys start with ['recurring-invoices', orgId] so an org switch refetches.
// ---------------------------------------------------------------------------

function useActiveOrgId(): string | undefined {
  return useAuthStore((s) => selectActiveOrg(s)?.id);
}

/**
 * Always fetched with `includeInactive` — the page hides paused schedules
 * locally so `ArchivedToggle`'s count is a real count and not a second request.
 */
export function useRecurringInvoices() {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['recurring-invoices', orgId],
    queryFn: () =>
      api.get<RecurringList>('/invoices/recurring', { params: { includeInactive: 1 } }),
    enabled: Boolean(orgId),
  });
}

/**
 * The subject preview, resolved by the server. One resolver, not two: the
 * string this shows while you type is the string the invoice will carry.
 */
export function useSubjectPreview(subject: string, date: string, netDays: number) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['recurring-invoices', orgId, 'preview', subject, date, netDays],
    queryFn: () =>
      api.post<{ issueDate: string; subject: string; unknownVariables: string[] }>(
        '/invoices/recurring/preview',
        { subject, date, netDays },
      ),
    enabled: Boolean(orgId) && subject.trim().length > 0,
    placeholderData: (previous) => previous,
    staleTime: 5 * 60_000,
  });
}

/** Everything that writes invalidates the invoices list too — a generated
 *  draft has to appear there, and the cash report's MRR row moves with it. */
function useRecurringMutation<TInput, TResult>(fn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  const orgId = useActiveOrgId();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurring-invoices', orgId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['cashflow', orgId] });
    },
  });
}

export function useCreateRecurring() {
  return useRecurringMutation((input: RecurringInput) =>
    api.post<RecurringTemplate>('/invoices/recurring', input),
  );
}

export function useUpdateRecurring() {
  return useRecurringMutation(({ id, ...input }: Partial<RecurringInput> & { id: string }) =>
    api.patch<RecurringTemplate>(`/invoices/recurring/${id}`, input),
  );
}

/** Credit the invoices a schedule already made to its agreement(s). */
export function useLinkPastRecurring() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, apply }: { id: string; apply: boolean }) =>
      api.post<LinkPastResult>(`/invoices/recurring/${id}/link-past`, { apply }),
    onSuccess: (_r, vars) => {
      if (!vars.apply) return;
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['agreements'] });
    },
  });
}

export function useDeleteRecurring() {
  return useRecurringMutation((id: string) => api.delete<unknown>(`/invoices/recurring/${id}`));
}

/** "Generate now" — one schedule, one DRAFT invoice. */
export function useGenerateRecurring() {
  return useRecurringMutation((id: string) =>
    api.post<GeneratedInvoice>(`/invoices/recurring/${id}/generate`, {}),
  );
}

/** Every occurrence that has come due, across all schedules. */
export function useRunDueRecurring() {
  return useRecurringMutation(() => api.post<RunResult>('/invoices/recurring/run', {}));
}

// ---------------------------------------------------------------------------
// Stripe subscriptions — link a schedule to the subscription that pays it
// ---------------------------------------------------------------------------

/** Read live from Stripe. Only fetched while the picker is open. */
export function useStripeSubscriptions(enabled: boolean) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['recurring-invoices', orgId, 'stripe-subscriptions'],
    queryFn: () => api.get<StripeSubscriptionList>('/invoices/recurring/stripe/subscriptions'),
    enabled: Boolean(orgId) && enabled,
    staleTime: 30_000,
    // "Stripe is not connected" does not improve on a retry.
    retry: 1,
  });
}

export function useLinkSubscription() {
  return useRecurringMutation(
    ({ templateId, subscriptionId }: { templateId: string; subscriptionId: string | null }) =>
      api.put<{ templateId: string; warnings: string[] }>(
        `/invoices/recurring/${templateId}/stripe`,
        { subscriptionId },
      ),
  );
}

/** The linked subscription's recent paid charges, with what recording each would do. */
export function useStripeCharges(templateId: string | null, enabled: boolean) {
  const orgId = useActiveOrgId();
  return useQuery({
    queryKey: ['recurring-invoices', orgId, 'stripe-charges', templateId],
    queryFn: () =>
      api.get<{ lookbackDays: number; charges: StripeChargeRow[]; recordable: number }>(
        `/invoices/recurring/${templateId}/stripe/charges`,
      ),
    enabled: Boolean(orgId) && Boolean(templateId) && enabled,
    retry: 1,
  });
}

export function useRecordStripeCharges() {
  return useRecurringMutation(({ templateId, ids }: { templateId: string; ids: string[] }) =>
    api.post<{
      recorded: number;
      results: Array<{ stripeInvoiceId: string; outcome: string; message: string }>;
    }>(`/invoices/recurring/${templateId}/stripe/charges`, { ids }),
  );
}
